import { useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Timer } from '@fff-web/fff-utilities';
import { type EnvConfig } from '../../environment';
import { type Timer as TimerType } from '../context/GlobalNavInternalContext';
import { useUniversalNavigationContext } from '../context/UniversalNavigationContext';
import {
  ACTIVE_CHECKOUT,
  PASSIVE_CHECKOUT,
  READY_FOR_SALE,
  TIMER_COMPLETED,
  USER_COMPLETED,
  USER_ONBOARDING_1,
  USER_ONBOARDING_2,
  USER_ONBOARDING_3,
  USER_ONBOARDING_4,
} from '../helpers/constants/newMemberStates';
import { newMemberStateUrlMap } from '../helpers/newMemberStateUrlMap';
import { getTimer } from '../services';
import { getHref, getPathname, setHref } from '../utils/location';

const withFinalSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`);

export function useNmfTimer(env: EnvConfig) {
  const { onTimerFetched } = useUniversalNavigationContext();

  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState<TimerType | null>(null);

  const NMFPages = newMemberStateUrlMap.map((mapItem) => mapItem.nmUrls).flat();

  const updateTimer = (timer: TimerType) => {
    setTimer(timer);
    onTimerFetched(timer);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const timer = await getTimer(env);
        // allow new member to navigate back to NMC if has completed NMF and Flash Sale is open
        if (
          [
            READY_FOR_SALE,
            USER_ONBOARDING_1,
            USER_ONBOARDING_2,
            USER_ONBOARDING_3,
            USER_ONBOARDING_4,
            USER_COMPLETED,
          ].includes(timer?.newMemberState) &&
          timer?.flashSaleId &&
          NMFPages.some((url) => withFinalSlash(url) === withFinalSlash(getPathname()))
        ) {
          updateTimer(timer);
        }
        // if there is no timer or time is up redirect new member to homepage
        else if (
          !timer ||
          (timer.endTime && Timer.getTimeLeft(new Date(`${timer.endTime}Z`)).timeUp) ||
          [
            READY_FOR_SALE,
            USER_ONBOARDING_1,
            USER_ONBOARDING_2,
            USER_ONBOARDING_3,
            USER_ONBOARDING_4,
            USER_COMPLETED,
            ACTIVE_CHECKOUT,
            PASSIVE_CHECKOUT,
            TIMER_COMPLETED,
          ].includes(timer.newMemberState)
        ) {
          if (getHref() !== env.REACT_APP_BASE_URL) {
            setHref(env.REACT_APP_BASE_URL);
          }
        }
        updateTimer(timer);
      } catch (err) {
        console.error(err);
        datadogLogs.logger.error('Unable to fetch timer', {}, err as Error);
        setLoading(false);
      }
    })();
  }, []);

  return { ...timer, loading };
}
